import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2, KeyRound, XCircle } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setErr("Passwords don't match"); return; }
    if (!token) { setErr("Missing reset token"); return; }
    setLoading(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login", { replace: true }), 1500);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setLoading(false);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 grid-bg">
      <Seo
        title="Set a New Password – DriveTerrain"
        description="Choose a new password and get back into your DriveTerrain account."
        canonical="/reset-password"
        robots="noindex, follow"
        twitterCard="summary"
      />
      <div className="w-full max-w-md bg-card border border-border p-8" data-testid="reset-password-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
          <KeyRound className="w-3.5 h-3.5 text-primary" /> Choose a new password
        </div>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Reset password</h1>

        {!token ? (
          <div className="mt-6 border border-destructive/40 bg-destructive/5 p-4 text-sm flex gap-3 items-start" data-testid="reset-no-token">
            <XCircle className="w-5 h-5 text-destructive mt-0.5" />
            <div>
              <div className="font-medium">Missing reset token</div>
              <div className="text-muted-foreground mt-1">Open this page from the link in your reset email.</div>
            </div>
          </div>
        ) : done ? (
          <div className="mt-6 border border-primary/40 bg-primary/5 p-4 text-sm flex gap-3 items-start" data-testid="reset-success">
            <CheckCircle2 className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <div className="font-medium">Password updated</div>
              <div className="text-muted-foreground mt-1">Taking you to sign in…</div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-6">
            <div>
              <Label>New password</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                     required minLength={6} className="rounded-sm" data-testid="reset-pw" />
              <div className="text-xs text-muted-foreground mt-1">At least 6 characters.</div>
            </div>
            <div>
              <Label>Confirm password</Label>
              <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                     required minLength={6} className="rounded-sm" data-testid="reset-pw2" />
            </div>
            <Button type="submit" className="w-full rounded-sm" disabled={loading} data-testid="reset-submit">
              {loading ? "Saving…" : "Save new password"}
            </Button>
            {err && <div className="text-sm text-destructive" data-testid="reset-error">{err}</div>}
          </form>
        )}

        <div className="mt-6 text-sm text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline" data-testid="reset-back-to-login">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
